import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';

import { createConsumer, INTERNAL } from '@rails/actioncable';
import { z } from 'zod';

import { notifyBugsnag } from '@/components/bugsnag';

import type { ChannelNameWithParams, Consumer, Subscription } from '@rails/actioncable';
import type { ReactNode } from 'react';

// The tab's ActionCable transport (docs/realtime/adr/0001-one-cable-consumer-per-tab.md).

export type CableStatus = 'connecting' | 'connected' | 'offline' | 'rejected';

/** Flat, so equal params always name the same subscription. */
export type ChannelParams = Readonly<Record<string, string | number | boolean>>;

const RECONNECT_JITTER_MS = 20_000;

const shellCableMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('invalidate'),
    // A tab from before a protocol change passes a newer payload through as unknown.
    v: z.literal(1),
    resource: z.string(),
    id: z.number().optional(),
    topic: z.string().optional(),
    version: z.number().optional(),
    by: z.object({ userId: z.number(), name: z.string() }).optional()
  }),
  z.object({ type: z.literal('version'), assetVersion: z.string().nullable() }),
  z.object({
    type: z.literal('roster'),
    record: z.string(),
    members: z.array(
      z.object({
        key: z.string(),
        userId: z.number(),
        name: z.string(),
        mode: z.enum(['viewing', 'editing', 'away'])
      })
    )
  }),
  z.object({
    type: z.literal('session'),
    event: z.literal('welcome'),
    userId: z.number(),
    entityId: z.number(),
    entityName: z.string(),
    accessRevision: z.string(),
    assetVersion: z.string().nullable()
  })
]);

export type ShellCableMessage =
  | z.infer<typeof shellCableMessageSchema>
  | { type: 'unknown'; payload: unknown };

export const parseShellCableMessage = (payload: unknown): ShellCableMessage => {
  const parsed = shellCableMessageSchema.safeParse(payload);
  return parsed.success ? parsed.data : { type: 'unknown', payload };
};

// ActionCable names a subscription by the JSON of its params, so sorted keys make equal params
// share one subscription.
const subscriptionIdentifier = (channel: string, params: ChannelParams): ChannelNameWithParams => ({
  channel,
  ...Object.fromEntries(Object.entries(params).sort(([a], [b]) => (a < b ? -1 : 1)))
});

// 5 minutes, or 5 seconds in development so the hidden-tab disconnect is easy to see.
const hiddenDisconnectDelay = (): number => {
  const railsEnv = document.querySelector('meta[name="rails-env"]');
  const env = railsEnv?.getAttribute('value') ?? railsEnv?.getAttribute('content');
  return (env === 'development' ? 5 : 5 * 60) * 1000;
};

interface Listener {
  received: (data: unknown) => void;
  status: (status: CableStatus) => void;
}

interface Entry {
  identifier: ChannelNameWithParams;
  status: CableStatus;
  subscription: Subscription | null;
  listeners: Set<Listener>;
  /** Records the status and tells every listener. */
  setStatus: (status: CableStatus) => void;
}

type ConnectionEvents = Record<string, (this: unknown, event: { data: string }) => void>;

const protocolFrameSchema = z.object({ type: z.string().optional() });

interface ConnectionWatch {
  /** The server accepted the socket. */
  welcomed: () => void;
  /** The socket is going down; the client reopens it only when `willReconnect` is true. */
  down: (willReconnect: boolean) => void;
}

// ActionCable reports the socket only to subscriptions, and the shell keeps its socket between
// pages, so the hub watches the connection itself.
const watchConnection = (consumer: Consumer, watch: ConnectionWatch) => {
  const connection = consumer.connection as unknown as {
    events: ConnectionEvents;
    monitor: { isRunning: () => boolean };
    close: (options?: { allowReconnect: boolean }) => unknown;
  };
  const { close, events } = connection;
  // Every close the client starts (a disconnect frame, a stale socket being reopened, the hub's
  // own) counts at once: a browser fires the close event later, and the socket may reopen first.
  connection.close = (options) => {
    const closing = close.call(connection, options);
    watch.down(connection.monitor.isRunning());
    return closing;
  };
  connection.events = {
    ...events,
    message(event) {
      events.message.call(this, event);
      const frame = protocolFrameSchema.safeParse(JSON.parse(event.data));
      if (frame.success && frame.data.type === INTERNAL.message_types.welcome) watch.welcomed();
    },
    // A socket the network closed, which no client call announced.
    close(event) {
      events.close.call(this, event);
      watch.down(connection.monitor.isRunning());
    }
  };
};

/** An action's payload; ActionCable adds `action` itself. */
export type ChannelActionData = Readonly<Record<string, unknown>>;

export interface CableHub {
  /** Adds a listener to the subscription for `identifier`, creating it first; returns the removal. */
  subscribe: (identifier: ChannelNameWithParams, listener: Listener) => () => void;
  /** Sends `action` on the subscription for `identifier` while it is connected; false otherwise. */
  perform: (identifier: ChannelNameWithParams, action: string, data: ChannelActionData) => boolean;
  /** Counts an owner; the consumer closes once the last owner releases it. */
  retain: () => () => void;
  onReconnect: (callback: () => void) => () => void;
  onStatusChange: (callback: () => void) => () => void;
  status: () => CableStatus;
}

/** `holdOpen` keeps the socket open while the tab is hidden; meant for unsaved changes. */
const createCableHub = (holdOpen: () => boolean = () => false): CableHub => {
  const entries = new Map<string, Entry>();
  const statusCallbacks = new Set<() => void>();
  const reconnectCallbacks = new Set<() => void>();
  let consumer: Consumer | null = null;
  let owners = 0;
  let paused = false;
  let refused = false;
  let status: CableStatus = 'connecting';
  let welcomedBefore = false;
  let hiddenTimer: ReturnType<typeof setTimeout> | undefined;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let releaseTimer: ReturnType<typeof setTimeout> | undefined;

  const run = (callback: () => void) => {
    try {
      callback();
    } catch (error) {
      // One subscriber's failure must not stop the others.
      notifyBugsnag(error);
    }
  };

  // The tab's status follows the socket, not which pages are subscribed: leaving a page must not
  // cancel a pending reconnect callback.
  const setStatus = (next: CableStatus) => {
    if (next === status) return;
    status = next;
    if (next !== 'connected') clearTimeout(reconnectTimer);
    statusCallbacks.forEach(run);
  };

  // Any welcome after the first is a reconnect, even when the old socket's close never arrived
  // (ActionCable drops a closing socket's handlers when it opens the next one).
  const onWelcome = () => {
    if (refused) return;
    const reconnected = welcomedBefore;
    welcomedBefore = true;
    setStatus('connected');
    if (!reconnected) return;
    // Every deploy drops every socket at once, so spread the catch-up refetches.
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(
      () => reconnectCallbacks.forEach(run),
      Math.random() * RECONNECT_JITTER_MS
    );
  };

  const refuse = () => {
    refused = true;
    clearTimeout(hiddenTimer);
    entries.forEach((entry) => entry.setStatus('rejected'));
    setStatus('rejected');
  };

  const onSocketDown = (willReconnect: boolean) => {
    if (refused) return;
    // Going down without reconnect, when the hub did not ask for it, is the server refusing it.
    if (!willReconnect && !paused) {
      refuse();
      return;
    }
    // ActionCable never calls disconnected for a socket the client replaces while it is still
    // closing, so the hub marks the subscriptions offline itself.
    entries.forEach((entry) => {
      if (entry.status !== 'rejected') entry.setStatus('offline');
    });
    setStatus('offline');
  };

  const pauseWhenHidden = () => {
    clearTimeout(hiddenTimer);
    hiddenTimer = setTimeout(() => {
      if (!consumer || refused) return;
      // ActionCable skips closing a socket that is still connecting but stops its reconnect monitor,
      // so wait until the socket settles.
      const connecting = consumer.connection.isActive() && !consumer.connection.isOpen();
      if (holdOpen() || connecting) {
        pauseWhenHidden();
        return;
      }
      paused = true;
      consumer.disconnect();
    }, hiddenDisconnectDelay());
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      pauseWhenHidden();
      return;
    }
    clearTimeout(hiddenTimer);
    if (!paused) return;
    paused = false;
    consumer?.connect();
  };

  const ensureConsumer = (): Consumer => {
    if (consumer) return consumer;
    const created = createConsumer();
    welcomedBefore = false;
    watchConnection(created, {
      // ActionCable skips closing a socket that is still connecting, so a released consumer's socket
      // can open anyway. Close it then.
      welcomed: () => {
        if (consumer !== created) created.disconnect();
        else onWelcome();
      },
      down: (willReconnect) => {
        if (consumer === created) onSocketDown(willReconnect);
      }
    });
    consumer = created;
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (document.visibilityState === 'hidden') pauseWhenHidden();
    return consumer;
  };

  const release = () => {
    const released = consumer;
    consumer = null;
    clearTimeout(hiddenTimer);
    clearTimeout(reconnectTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    released?.disconnect();
    paused = false;
    if (!refused) setStatus('connecting');
  };

  const open = (entry: Entry): Subscription => {
    const owner = ensureConsumer();
    // Creating a subscription reopens a socket closed while hidden.
    if (paused) {
      paused = false;
      if (document.visibilityState === 'hidden') pauseWhenHidden();
    }
    // A released consumer's late callbacks must not touch the entries.
    const current = () => consumer === owner;
    return owner.subscriptions.create(entry.identifier, {
      connected: () => {
        if (current()) entry.setStatus('connected');
      },
      rejected: () => {
        if (current()) entry.setStatus('rejected');
      },
      received: (data: unknown) => {
        if (!current()) return;
        [...entry.listeners].forEach((listener) => run(() => listener.received(data)));
      }
    });
  };

  const addEntry = (key: string, identifier: ChannelNameWithParams): Entry => {
    const entry: Entry = {
      identifier,
      status: refused ? 'rejected' : 'connecting',
      subscription: null,
      listeners: new Set(),
      setStatus: (next) => {
        entry.status = next;
        entry.listeners.forEach((listener) => run(() => listener.status(next)));
      }
    };
    entries.set(key, entry);
    if (!refused) entry.subscription = open(entry);
    return entry;
  };

  return {
    subscribe: (identifier, listener) => {
      const key = JSON.stringify(identifier);
      const entry = entries.get(key) ?? addEntry(key, identifier);
      entry.listeners.add(listener);
      listener.status(entry.status);
      return () => {
        if (!entry.listeners.delete(listener) || entry.listeners.size > 0) return;
        if (entries.get(key) === entry) entries.delete(key);
        // ActionCable has already forgotten a subscription the server rejected.
        if (entry.status !== 'rejected') entry.subscription?.unsubscribe();
      };
    },
    perform: (identifier, action, data) => {
      const entry = entries.get(JSON.stringify(identifier));
      if (!entry?.subscription || entry.status !== 'connected') return false;
      // ActionCable writes `action` into the object it is given.
      return entry.subscription.perform(action, { ...data });
    },
    retain: () => {
      owners += 1;
      clearTimeout(releaseTimer);
      let released = false;
      return () => {
        if (released) return;
        released = true;
        owners -= 1;
        // A remount in the same commit (StrictMode, a page swap) keeps the consumer.
        if (owners === 0) releaseTimer = setTimeout(release, 0);
      };
    },
    onReconnect: (callback) => {
      reconnectCallbacks.add(callback);
      return () => reconnectCallbacks.delete(callback);
    },
    onStatusChange: (callback) => {
      statusCallbacks.add(callback);
      return () => statusCallbacks.delete(callback);
    },
    status: () => status
  };
};

/** The shell's hub for this tab, or null while `shell_cable` is off for the entity. */
export const createShellCable = (enabled: boolean, holdOpen?: () => boolean): CableHub | null =>
  enabled ? createCableHub(holdOpen) : null;

// Outside the shell every React root on the page shares one hub, so one consumer.
const documentHubs = new WeakMap<Document, CableHub>();

const documentHub = (): CableHub => {
  const existing = documentHubs.get(document);
  if (existing) return existing;
  const hub = createCableHub();
  documentHubs.set(document, hub);
  return hub;
};

// Undefined outside the shell.
const ShellCableContext = createContext<{ hub: CableHub | null } | undefined>(undefined);

export const ShellCableProvider = ({
  cable,
  children
}: {
  cable: CableHub | null;
  children: ReactNode;
}) => {
  // The shell owns its hub, so the consumer outlives every page it shows.
  useEffect(() => cable?.retain(), [cable]);
  const value = useMemo(() => ({ hub: cable }), [cable]);
  return <ShellCableContext.Provider value={value}>{children}</ShellCableContext.Provider>;
};

const useShellHub = (): CableHub | null => {
  const shell = useContext(ShellCableContext);
  return shell ? shell.hub : documentHub();
};

// Data channels have no polling fallback, so shell_cable never switches them off.
const useDataHub = (): CableHub => useContext(ShellCableContext)?.hub ?? documentHub();

const useSubscription = (
  hub: CableHub | null,
  channel: string,
  params: ChannelParams | null,
  onData: (data: unknown) => void
): CableStatus => {
  const key = params && JSON.stringify(subscriptionIdentifier(channel, params));
  const [current, setCurrent] = useState<{ key: string; status: CableStatus } | null>(null);
  const handler = useRef(onData);
  useLayoutEffect(() => {
    handler.current = onData;
  });
  useEffect(() => {
    if (!hub || key === null) return undefined;
    return hub.subscribe(JSON.parse(key) as ChannelNameWithParams, {
      received: (data) => handler.current(data),
      status: (status) => setCurrent({ key, status })
    });
  }, [hub, key]);
  useEffect(() => hub?.retain(), [hub]);

  if (!hub) return 'rejected';
  if (key === null) return 'offline';
  return current?.key === key ? current.status : 'connecting';
};

/**
 * Subscribes to a shell channel on the tab's one consumer while `params` is not null. Components
 * asking for the same channel and params share one subscription, and the latest `onReceive`
 * gets each message parsed into `ShellCableMessage`. Returns the subscription's status: anything
 * but `connected` means keep polling. Inside the shell with `shell_cable` off it subscribes to
 * nothing and returns `rejected`.
 */
export const useShellChannel = (
  channel: string,
  params: ChannelParams | null,
  onReceive: (message: ShellCableMessage) => void
): CableStatus =>
  useSubscription(useShellHub(), channel, params, (data) =>
    onReceive(parseShellCableMessage(data))
  );

/**
 * Like `useShellChannel`, for the per-user data channels behind `useWebSockets`: `onData` gets
 * each payload as the server sent it, and `shell_cable` does not switch it off.
 */
export const useDataChannel = (
  channel: string,
  params: ChannelParams | null,
  onData: (data: unknown) => void
): CableStatus => useSubscription(useDataHub(), channel, params, onData);

/**
 * Sends actions on the subscription `useShellChannel` holds for the same channel and params. The
 * returned function answers false, sending nothing, unless that subscription is connected.
 */
export const useShellPerform = (
  channel: string,
  params: ChannelParams | null
): ((action: string, data?: ChannelActionData) => boolean) => {
  const hub = useShellHub();
  const key = params && JSON.stringify(subscriptionIdentifier(channel, params));
  return useCallback(
    (action: string, data: ChannelActionData = {}) => {
      if (!hub || key === null) return false;
      return hub.perform(JSON.parse(key) as ChannelNameWithParams, action, data);
    },
    [hub, key]
  );
};

const noStatusChanges = () => () => undefined;
const rejectedStatus = (): CableStatus => 'rejected';

/**
 * The tab's connection for shell channels: `connected` once the server welcomed the current socket,
 * `offline` after it drops, `rejected` after the server refuses the connection.
 */
export const useCableStatus = (): CableStatus => {
  const hub = useShellHub();
  return useSyncExternalStore(
    hub?.onStatusChange ?? noStatusChanges,
    hub?.status ?? rejectedStatus
  );
};

/**
 * Runs the latest `onReconnect` once after each reconnect, with a random delay of up to 20
 * seconds so a deploy's reconnects spread out. Refetch what the socket may have missed here.
 */
export const useCableReconnect = (onReconnect: () => void): void => {
  const hub = useShellHub();
  const handler = useRef(onReconnect);
  useLayoutEffect(() => {
    handler.current = onReconnect;
  });
  useEffect(() => hub?.onReconnect(() => handler.current()), [hub]);
};
